import { boundsOf, type Camera, type Point, type Rect } from "@worktools/shared";

export type Size = { width: number; height: number };

export type MinimapProjection = {
  /** Canvas units per minimap pixel. */
  scale: number;
  origin: Point;
  blocks: Rect[];
  viewport: Rect;
};

const PADDING = 6;

function visibleRect(camera: Camera, viewport: Size): Rect {
  return {
    x: -camera.x,
    y: -camera.y,
    w: viewport.width / camera.zoom,
    h: viewport.height / camera.zoom,
  };
}

/** Lays out blocks and the current viewport inside the small map. */
export function minimapProjection(
  blocks: Rect[],
  camera: Camera,
  viewport: Size,
  size: Size,
): MinimapProjection | null {
  const seen = visibleRect(camera, viewport);
  const bounds = boundsOf(blocks.length > 0 ? [...blocks, seen] : []);
  if (!bounds) return null;

  const inner = { width: size.width - PADDING * 2, height: size.height - PADDING * 2 };
  const scale = Math.min(inner.width / Math.max(bounds.w, 1), inner.height / Math.max(bounds.h, 1));
  const offset = {
    x: PADDING + (inner.width - bounds.w * scale) / 2,
    y: PADDING + (inner.height - bounds.h * scale) / 2,
  };

  const project = (rect: Rect): Rect => ({
    x: (rect.x - bounds.x) * scale + offset.x,
    y: (rect.y - bounds.y) * scale + offset.y,
    w: rect.w * scale,
    h: rect.h * scale,
  });

  return {
    scale,
    origin: { x: bounds.x - offset.x / scale, y: bounds.y - offset.y / scale },
    blocks: blocks.map(project),
    viewport: project(seen),
  };
}

/** Turns a click in the minimap into a camera centred on that spot. */
export function minimapToCanvas(
  point: Point,
  projection: MinimapProjection,
  camera: Camera,
  viewport: Size,
): Camera {
  const target = {
    x: projection.origin.x + point.x / projection.scale,
    y: projection.origin.y + point.y / projection.scale,
  };
  return {
    zoom: camera.zoom,
    x: viewport.width / 2 / camera.zoom - target.x,
    y: viewport.height / 2 / camera.zoom - target.y,
  };
}
